/**
 * api.ts
 * Centralized Canonical REST API Client for VAXA Platform
 * Authenticated via session cookies (credentials: 'include') against relative /api/* routes.
 */

export interface AuthUser {
  id: string;
  uid?: string;
  email: string;
  display_name?: string | null;
  displayName?: string | null;
  photo_url?: string | null;
  photoURL?: string | null;
  is_admin?: boolean;
  isAdmin?: boolean;
  is_google_user?: boolean;
  auth_provider?: 'google' | 'password';
}

export interface AuthResponse {
  success: boolean;
  userId?: string;
  email?: string;
  user?: AuthUser;
  error?: string;
  message?: string;
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  count?: number;
  id?: string;
  deletedId?: string;
  updatedFields?: string[];
  error?: string;
  message?: string;
  status?: number;
}

// Storage key for transient startup UI caching only (NOT authoritative auth)
const AUTH_STORAGE_KEY = 'progress_worker_user';
const AUTH_TOKEN_KEY = 'progress_session_token';

export function getStoredToken(): string | null {
  try {
    return localStorage.getItem(AUTH_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setStoredToken(token: string | null): void {
  try {
    if (!token) {
      localStorage.removeItem(AUTH_TOKEN_KEY);
    } else {
      localStorage.setItem(AUTH_TOKEN_KEY, token.trim());
    }
  } catch {}
}

export function getStoredUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const userId = parsed.id || parsed.uid || parsed.userId;
    if (!userId) return null;
    return {
      ...parsed,
      id: userId,
      uid: userId
    };
  } catch {
    return null;
  }
}

export function setStoredUser(user: AuthUser | null): void {
  if (!user) {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    setStoredToken(null);
  } else {
    const userId = user.id || (user as any).uid || (user as any).userId;
    const normalizedUser: AuthUser = {
      id: userId,
      uid: userId,
      email: user.email,
      display_name: user.display_name || user.displayName || null,
      displayName: user.displayName || user.display_name || null,
      photo_url: user.photo_url || user.photoURL || null,
      photoURL: user.photoURL || user.photo_url || null,
      is_admin: Boolean(user.is_admin || (user as any).isAdmin),
      isAdmin: Boolean(user.is_admin || (user as any).isAdmin)
    };
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(normalizedUser));
  }
}

// ---------------- CENTRAL HTTP HELPERS ----------------

/**
 * Standard fetch wrapper with automatic cookie session transmission and robust error extraction.
 */
async function request<T = any>(url: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string> || {})
  };

  const token = getStoredToken();
  if (token) {
    if (!headers['Authorization']) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    if (!headers['X-Session-Token']) {
      headers['X-Session-Token'] = token;
    }
  }

  if (options.body && typeof options.body === 'string' && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  try {
    const res = await fetch(url, {
      ...options,
      headers,
      credentials: 'include'
    });

    if (res.status === 204) {
      return { success: true, status: 204 };
    }

    let data: any = null;
    const contentType = res.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      data = await res.json().catch(() => null);
    } else {
      const text = await res.text().catch(() => '');
      data = { message: text };
    }

    if (!res.ok) {
      const errorMsg = data?.error || data?.message || `HTTP ${res.status}: ${res.statusText}`;
      return {
        success: false,
        status: res.status,
        error: errorMsg,
        data: data?.data
      };
    }

    return {
      success: true,
      status: res.status,
      ...(data && typeof data === 'object' ? data : { data })
    };
  } catch (err: any) {
    console.error(`[API Error ${options.method || 'GET'} ${url}]:`, err);
    return {
      success: false,
      status: 0,
      error: err?.message || 'خطا در برقراری ارتباط با سرور'
    };
  }
}

export async function apiGet<T = any>(endpoint: string, params?: Record<string, any>): Promise<ApiResponse<T>> {
  let url = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  if (params && Object.keys(params).length > 0) {
    const q = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null) {
        q.append(key, String(value));
      }
    }
    const qs = q.toString();
    if (qs) url += `?${qs}`;
  }
  return request<T>(url, { method: 'GET' });
}

export async function apiPost<T = any>(endpoint: string, body?: any): Promise<ApiResponse<T>> {
  const url = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return request<T>(url, {
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined
  });
}

export async function apiPut<T = any>(endpoint: string, body?: any): Promise<ApiResponse<T>> {
  const url = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return request<T>(url, {
    method: 'PUT',
    body: body ? JSON.stringify(body) : undefined
  });
}

export async function apiDelete<T = any>(endpoint: string, params?: Record<string, any>): Promise<ApiResponse<T>> {
  let url = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  if (params && Object.keys(params).length > 0) {
    const q = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null) {
        q.append(key, String(value));
      }
    }
    const qs = q.toString();
    if (qs) url += `?${qs}`;
  }
  return request<T>(url, { method: 'DELETE' });
}

export async function apiUpload<T = any>(endpoint: string, formData: FormData): Promise<ApiResponse<T>> {
  const url = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  try {
    const res = await fetch(url, {
      method: 'POST',
      body: formData,
      credentials: 'include'
    });
    const data = await res.json().catch(() => null);
    if (!res.ok) {
      return {
        success: false,
        status: res.status,
        error: data?.error || data?.message || `Upload failed (HTTP ${res.status})`
      };
    }
    return {
      success: true,
      status: res.status,
      ...data
    };
  } catch (err: any) {
    return {
      success: false,
      status: 0,
      error: err?.message || 'خطا در ارسال فایل'
    };
  }
}

// ---------------- CANONICAL AUTHENTICATION API ----------------

/**
 * Authoritatively retrieves current authenticated session identity from server.
 */
export async function fetchAuthMe(): Promise<{ authenticated: boolean; user: AuthUser | null; error?: string }> {
  try {
    const res = await apiGet<any>('/api/auth/me');
    const rawUser = (res as any)?.user || res.data?.user || res.data;
    if (res.success && rawUser && (rawUser.id || rawUser.user_id || rawUser.uid)) {
      const normalizedUser: AuthUser = {
        id: rawUser.id || rawUser.user_id || rawUser.uid,
        uid: rawUser.id || rawUser.user_id || rawUser.uid,
        email: rawUser.email,
        display_name: rawUser.display_name || rawUser.displayName || null,
        displayName: rawUser.display_name || rawUser.displayName || null,
        photo_url: rawUser.photo_url || rawUser.photoURL || null,
        photoURL: rawUser.photo_url || rawUser.photoURL || null,
        is_admin: Boolean(rawUser.is_admin || rawUser.isAdmin),
        isAdmin: Boolean(rawUser.is_admin || rawUser.isAdmin),
        is_google_user: Boolean(rawUser.is_google_user),
        auth_provider: rawUser.auth_provider || (rawUser.is_google_user ? 'google' : 'password')
      };
      setStoredUser(normalizedUser);
      return { authenticated: true, user: normalizedUser };
    }
    if (res.status === 401) {
      setStoredUser(null);
      return { authenticated: false, user: null };
    }
    return { authenticated: false, user: null, error: res.error };
  } catch (err: any) {
    return { authenticated: false, user: null, error: err?.message };
  }
}

export async function registerWithWorker(email: string, password: string, displayName?: string): Promise<AuthResponse> {
  const res = await apiPost<any>('/api/auth/register', { email, password, display_name: displayName });
  const rawUser = (res as any)?.user || res.data?.user || res.data;
  const token = (res as any)?.session_token || res.data?.session_token;
  if (token) {
    setStoredToken(token);
  }
  if (res.success && rawUser && (rawUser.id || rawUser.user_id || rawUser.uid)) {
    const userObj: AuthUser = {
      id: rawUser.id || rawUser.user_id || rawUser.uid,
      email: rawUser.email || email,
      display_name: rawUser.display_name || rawUser.displayName || displayName || null,
      displayName: rawUser.display_name || rawUser.displayName || displayName || null,
      photo_url: rawUser.photo_url || rawUser.photoURL || null,
      photoURL: rawUser.photo_url || rawUser.photoURL || null,
      is_admin: Boolean(rawUser.is_admin || rawUser.isAdmin)
    };
    setStoredUser(userObj);
    return { success: true, user: userObj, userId: userObj.id };
  }
  return { success: false, error: res.error || 'خطا در ثبت‌نام' };
}

export async function loginWithWorker(email: string, password: string): Promise<AuthResponse> {
  const res = await apiPost<any>('/api/auth/login', { email, password });
  const rawUser = (res as any)?.user || res.data?.user || res.data;
  const token = (res as any)?.session_token || res.data?.session_token;
  if (token) {
    setStoredToken(token);
  }
  if (res.success && rawUser && (rawUser.id || rawUser.user_id || rawUser.uid)) {
    const userObj: AuthUser = {
      id: rawUser.id || rawUser.user_id || rawUser.uid,
      email: rawUser.email || email,
      display_name: rawUser.display_name || rawUser.displayName || null,
      displayName: rawUser.display_name || rawUser.displayName || null,
      photo_url: rawUser.photo_url || rawUser.photoURL || null,
      photoURL: rawUser.photo_url || rawUser.photoURL || null,
      is_admin: Boolean(rawUser.is_admin || rawUser.isAdmin)
    };
    setStoredUser(userObj);
    return { success: true, user: userObj, userId: userObj.id };
  }
  return { success: false, error: res.error || 'ایمیل یا رمز عبور اشتباه است.' };
}

export async function verifyGoogleTokenWithWorker(idToken: string): Promise<AuthResponse> {
  // Use canonical /api/auth/google endpoint (also supported via /api/auth/google/verify)
  const res = await apiPost<any>('/api/auth/google', { token: idToken });
  const rawUser = (res as any)?.user || res.data?.user || res.data;
  const token = (res as any)?.session_token || res.data?.session_token;
  if (token) {
    setStoredToken(token);
  }
  if (res.success && rawUser && (rawUser.id || rawUser.user_id || rawUser.uid)) {
    const userObj: AuthUser = {
      id: rawUser.id || rawUser.user_id || rawUser.uid,
      email: rawUser.email,
      display_name: rawUser.display_name || rawUser.displayName || null,
      displayName: rawUser.display_name || rawUser.displayName || null,
      photo_url: rawUser.photo_url || rawUser.photoURL || null,
      photoURL: rawUser.photo_url || rawUser.photoURL || null,
      is_admin: Boolean(rawUser.is_admin || rawUser.isAdmin)
    };
    setStoredUser(userObj);
    return { success: true, user: userObj, userId: userObj.id };
  }
  return { success: false, error: res.error || 'خطا در تایید هویت گوگل' };
}

export async function logoutUser(): Promise<void> {
  try {
    await apiPost('/api/auth/logout', {});
  } finally {
    setStoredToken(null);
    setStoredUser(null);
  }
}

export async function fetchWeightMeasurements(params?: { start_date?: string; end_date?: string; limit?: number }) {
  return apiGet<any[]>('/api/weight', params);
}

export async function saveWeightMeasurement(payload: {
  date: string;
  weight_kg: number;
  bmi?: number | null;
  body_fat_pct?: number | null;
  notes?: string;
}) {
  return apiPost<any>('/api/weight', payload);
}

export async function deleteWeightMeasurement(idOrDate: string) {
  return apiDelete<any>(`/api/weight/${idOrDate}`);
}

/**
 * Compresses an image client-side if it exceeds maxSizeBytes (600 KB = 614,400 bytes).
 * If the image cannot be compressed below maxSizeBytes, throws an error.
 */
export async function compressImageIfNeeded(file: File, maxSizeBytes: number = 614400): Promise<File> {
  if (file.size <= maxSizeBytes) {
    return file;
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('خطا در خواندن فایل تصویر.'));
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = () => reject(new Error('فرمت تصویر نامعتبر است.'));
      img.onload = () => {
        let width = img.width;
        let height = img.height;
        const maxDim = 1600;

        if (width > maxDim || height > maxDim) {
          if (width > height) {
            height = Math.round((height * maxDim) / width);
            width = maxDim;
          } else {
            width = Math.round((width * maxDim) / height);
            height = maxDim;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('امکان پردازش تصویر در مرورگر وجود ندارد.'));
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);

        const attemptCanvasToBlob = (quality: number, currentScale: number) => {
          if (currentScale < 1.0) {
            canvas.width = Math.round(width * currentScale);
            canvas.height = Math.round(height * currentScale);
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          }

          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('خطا در فشرده‌سازی تصویر.'));
                return;
              }

              if (blob.size <= maxSizeBytes) {
                const compressedFile = new File([blob], file.name.replace(/\.[^/.]+$/, "") + ".jpg", {
                  type: 'image/jpeg',
                  lastModified: Date.now()
                });
                resolve(compressedFile);
              } else if (quality > 0.4) {
                attemptCanvasToBlob(quality - 0.2, currentScale);
              } else if (currentScale > 0.5) {
                attemptCanvasToBlob(0.7, currentScale - 0.25);
              } else {
                reject(new Error(`حجم تصویر انتخاب شده (${(file.size / 1024).toFixed(0)} KB) پس از فشرده‌سازی نیز بیش از حد مجاز ۶۰۰ کیلوبایت است.`));
              }
            },
            'image/jpeg',
            quality
          );
        };

        attemptCanvasToBlob(0.82, 1.0);
      };
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  });
}

import { uploadFileToWorker } from './workerFileStorage';

export async function uploadWeightPhoto(measurementId: string, file: File, photoType: string = 'other') {
  if (!measurementId || measurementId === 'undefined' || measurementId === 'null') {
    throw new Error('شناسه ثبت وزن نامعتبر است.');
  }

  // 1. Client compression & strict 600 KB size enforcement
  const processedFile = await compressImageIfNeeded(file, 614400);

  if (processedFile.size > 614400) {
    throw new Error(`حجم فایل تصویر نباید بیشتر از ۶۰۰ کیلوبایت (600 KB) باشد.`);
  }

  // 2. Canonical Upload directly to Worker/R2 (identical to Learning Assistant)
  const workerRes = await uploadFileToWorker(processedFile, 'weight_photos', measurementId);

  if (!workerRes || !workerRes.success || !workerRes.fileId) {
    throw new Error(workerRes?.error || 'خطا در بارگذاری تصویر در فضای ذخیره‌سازی ابری.');
  }

  // 3. Establish Weight Measurement <-> Photo relationship in D1
  const linkRes = await apiPost<any>(`/api/weight/${measurementId}/photos`, {
    fileId: workerRes.fileId,
    photoType: photoType || 'other'
  });

  if (!linkRes || !linkRes.success) {
    throw new Error(linkRes?.error || 'خطا در اتصال تصویر به ثبت وزن.');
  }

  return linkRes;
}

export async function deleteWeightPhoto(photoId: string) {
  return apiDelete<any>(`/api/weight/photos/${photoId}`);
}



